import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { REMONDIS_LOGO } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogIn, Loader2, ShieldCheck } from "lucide-react";

export default function AdminLogin() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  if (loading) return null;
  if (user && user !== false) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      navigate("/admin");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] grid lg:grid-cols-2">
      {/* Visual side */}
      <div className="relative hidden lg:flex items-end p-12 overflow-hidden bg-slate-900">
        <div
          className="absolute inset-0 opacity-50"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1768128834406-f5b1b962af9d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxpbmR1c3RyaWFsJTIwZmFjaWxpdHl8ZW58MHx8fHwxNzgyMDUyMzYwfDA&ixlib=rb-4.1.0&q=85')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-slate-950/20" />
        <div className="absolute top-0 left-0 w-32 h-1.5 bg-[#E2001A]" />
        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur text-white text-xs font-semibold uppercase tracking-wider mb-4 border border-white/20">
            <ShieldCheck className="h-3.5 w-3.5" />
            Geschützter Bereich
          </div>
          <h2 className="font-heading text-4xl font-bold text-white max-w-md leading-tight">
            Anlagen-Updates verwalten
          </h2>
          <p className="text-slate-300 mt-3 max-w-md text-balance">
            Veröffentlichen Sie Wartungstermine, Störungsmeldungen und Informationen — schnell und übersichtlich.
          </p>
        </div>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-8 sm:p-12 bg-white">
        <div className="w-full max-w-md">
          <img src={REMONDIS_LOGO} alt="REMONDIS" className="h-16 w-auto mb-8 object-contain" />
          <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight">Admin-Anmeldung</h1>
          <p className="text-slate-600 mt-2">Bitte mit Ihren Zugangsdaten anmelden.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">E-Mail</Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@remondis.de"
                className="border-slate-300 h-11"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">Passwort</Label>
              <Input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="border-slate-300 h-11"
                data-testid="login-password-input"
              />
            </div>

            {error && (
              <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="login-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-11 bg-[#E2001A] hover:bg-[#C20015] text-white font-semibold"
              data-testid="login-submit-button"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Wird angemeldet…
                </>
              ) : (
                <>
                  <LogIn className="h-4 w-4 mr-2" />
                  Anmelden
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
